import type { MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import { DATASETS } from '@/components/Constants';
import { searchContext, userAppContext } from '../../../../context/Context';
import { getItemPresentation, getMatchSnippet, getSelectionTagLabel, highlightQueryParts } from '../helpers';
import { SearchContext } from '../../../../types';
import { UserAppContextType } from '../../../../router/types';
import {
  isNewTabModifierClick,
  isSearchWorkspacePath,
  useSearchTabNavigation,
} from '@/hooks/search/useSearchTabNavigation';

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  const parts = highlightQueryParts(text, query);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          <mark key={`${part.text}-${index}`} className="search-dropdown-highlight">
            {part.text}
          </mark>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        ),
      )}
    </>
  );
};

const SectionItem = ({
  item,
  query,
  setValue,
  isFocused,
  optionId,
  navIndex,
}: {
  item: any;
  query: string;
  setValue: (value: string) => void;
  isFocused: boolean;
  optionId: string;
  navIndex: number;
}) => {
  const location = useLocation();
  const { navigateSearch } = useSearchTabNavigation();
  const patched = patchItems(item);
  const { searchItems, setSearchItems } = searchContext() as unknown as SearchContext;
  const { restrictSources, groups } = userAppContext() as unknown as UserAppContextType;
  const presentation = getItemPresentation(patched);
  const matchSnippet = getMatchSnippet(patched);
  const isChecked = searchItems!.some(prevItem => prevItem.id === patched.id);

  const handleClick = () => {
    setSearchItems!(prevItems =>
      prevItems.some(prevItem => prevItem.id === patched.id)
        ? prevItems.filter(prevItem => prevItem.id !== patched.id)
        : [...prevItems, patched],
    );
  };

  const handleGoToItem = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const view = patched.dataset || DATASETS.DISEASES;
    const tab = view === DATASETS.VARIANTS ? 'VDA' : 'GDA';
    const isAcademic = groups?.includes('ROLE_ACADEMIC');
    const source = restrictSources === 'CURATED' || isAcademic ? 'CURATED' : 'ALL';
    const search = `?view=${view}&idents=${patched.id}&source=${source}&tab=${tab}`;
    const title = getSelectionTagLabel(patched);
    const openInNewTab = !isSearchWorkspacePath(location.pathname) || isNewTabModifierClick(event);

    const result = navigateSearch({
      search,
      title,
      searchItems: [patched],
      openInNewTab,
    });
    if (!result.ok && openInNewTab) {
      navigateSearch({
        search,
        title,
        searchItems: [patched],
        openInNewTab: false,
      });
    }

    setValue('');
  };

  return (
    <li
      id={optionId}
      role="option"
      aria-selected={isChecked}
      data-nav-index={navIndex}
      className={`search-dropdown-item${isFocused ? ' is-focused' : ''}${isChecked ? ' is-selected' : ''}`}
      onClick={handleClick}
      onMouseDown={event => event.preventDefault()}
    >
      <input
        type="checkbox"
        className="search-dropdown-item__checkbox"
        checked={isChecked}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
      <div className="search-dropdown-item__body">
        <div className="search-dropdown-item__title">
          <HighlightedText text={presentation.title} query={query} />
        </div>
        {presentation.subtitle ? (
          <div className="search-dropdown-item__subtitle">{presentation.subtitle}</div>
        ) : null}
        {(presentation.tags.length > 0 || matchSnippet) && (
          <div className="search-dropdown-item__meta">
            {presentation.tags.length > 0 && (
              <div className="search-dropdown-item__tags">
                {presentation.tags.map(tag => (
                  <span key={tag} className="search-dropdown-item__tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {matchSnippet ? <span className="search-dropdown-item__match">{matchSnippet}</span> : null}
          </div>
        )}
      </div>
      <button
        type="button"
        className="search-dropdown-item__arrow-btn"
        aria-label={`Search ${presentation.title || patched.id}`}
        title="Go to this result"
        onMouseDown={event => event.preventDefault()}
        onClick={handleGoToItem}
      >
        <ArrowRightIcon className="search-dropdown-item__arrow" aria-hidden />
      </button>
    </li>
  );
};

export default SectionItem;
